/* eslint-disable no-console */
import { Ref, ref } from "@vue/composition-api";
import { Conversation, SessionConfig } from "@11labs/client";
import { Call, CallType } from "@scrile/api-provider/dist/api/CallsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import providers from "@/lib/api";
import { connectionStateEnum } from "@/const";

export function isAiCall(call: Call | null) {
  return call?.subject.subjectType === CallType.AI_CALL;
}

export function useAICalls(state: Ref<connectionStateEnum>, callEnd: () => void) {
  const conversation = ref<Conversation | null>(null);
  const conversationSessionData = ref<SessionConfig>({
    signedUrl: "",
    overrides: {},
    dynamicVariables: { callId: "" },
  });

  const getAiCallEnabled = async (userId: string) => {
    const response = await providers.AiSettingsProvider.userPublicSettingsSearch({ userId });
    return response.voiceCallEnabled;
  };

  const end = async () => {
    if (!conversation.value) throw new Error("conversation wasn't started");
    await conversation.value.endSession();
    conversation.value = null;
  };

  const start = async (call: Ref<Call | null>) => {
    if (!call.value) return null;
    const fake_connection = {
      userKey: call.value.callerId,
      producerUrl: null,
      consumerUrl: null,
      opponentKey: call.value.receiverId,
    };

    conversation.value = await Conversation.startSession({
      ...conversationSessionData.value,
      onConnect: async props => {
        if (!call.value) return;
        console.info("onConnect", props, call.value);
        await providers.AiCallsProvider.start({ data: { callId: call.value.id } });
      },
      onStatusChange: prop => {
        if (["connecting", "connected", "disconnected"].includes(prop.status)) {
          state.value = prop.status as connectionStateEnum;
        }
        console.info("onStatusChange", prop);
      },
      onError: (message, context) => {
        end();
        callEnd();
        console.error("onError", message, context);
      },
      onDisconnect: props => {
        end();
        callEnd();
        console.info("onDisconnect", props);
      },
      // only for debug
      onDebug: props => console.info("onDebug", props),
      onMessage: props => console.info("onMessage", props),
      onModeChange: prop => console.info("onModeChange", prop),
      onCanSendFeedbackChange: prop => console.info("onCanSendFeedbackChange", prop),
      onUnhandledClientToolCall: params => console.info("onUnhandledClientToolCall", params),
    });
    conversation.value.setVolume({ volume: 1 });

    return fake_connection;
  };

  const create = async (user: UserPublicData) => {
    const {
      signedUrl,
      voiceId,
      callId,
      call: newCall,
      welcomeMessage,
      prompt,
    } = await providers.AiCallsProvider.create({ userId: user.id });

    const overrides: SessionConfig["overrides"] = {};
    if (voiceId) {
      overrides.tts = { voiceId };
    }
    if (welcomeMessage) {
      overrides.agent = { ...overrides.agent, firstMessage: welcomeMessage };
    }
    if (prompt) {
      overrides.agent = { ...overrides.agent, prompt: { prompt } };
    }
    conversationSessionData.value = {
      signedUrl,
      overrides,
      dynamicVariables: { callId },
    };
    return newCall;
  };

  return {
    state,
    getAiCallEnabled,
    create,
    start,
    end,
  };
}
